/**
 * Hand an attachment on to another session (RFC 0336 S11).
 *
 * An agent never sees an attachment as a document: the harness writes it to
 * `<tmp>/mod-attachments/<docId>/<name>` and hands over the PATH (RFC 0288,
 * `materializeAttachments`). So an agent asked to pass a screenshot along has
 * a path in hand, and `send_message` used to take text only, which put the
 * path in the text and left the picture behind.
 *
 * The path already names the document, and an attachment document is
 * immutable, so forwarding is a POINTER to the same document: no bytes read,
 * nothing re-uploaded, no file system in core. A bare document id works too.
 * A path that is not a Mod attachment is refused by name, rather than sent as
 * a picture that never arrives.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { AttachmentRef } from '../log/types'

/** The cache directory segment every harness writes attachments under. */
const CACHE_SEGMENT = 'mod-attachments'

/**
 * The attachment document a path or id names, or null when it names none.
 *
 * `…/mod-attachments/<docId>/<name>` gives `<docId>`; a value with no path
 * separator is taken as the id itself.
 */
export function attachmentDocId(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parts = trimmed.split(/[\\/]+/).filter(Boolean)
  const at = parts.lastIndexOf(CACHE_SEGMENT)
  if (at !== -1) return parts[at + 1] ?? null
  return parts.length === 1 && !trimmed.includes('.') ? trimmed : null
}

interface AttachmentDocShape {
  name?: unknown
  mimeType?: unknown
  content?: { byteLength?: number; length?: number }
  width?: unknown
  height?: unknown
}

/** The one call this makes against a repo. */
export interface ForwardRepo {
  find(id: AnyDocumentId): Promise<{
    whenReady?: () => Promise<void>
    doc(): unknown
  }>
}

/**
 * Pointers for the attachments named, in order. Throws, naming the first
 * value that is not a Mod attachment or whose document cannot be read, so the
 * agent can say what did not go rather than send half of it silently.
 */
export async function forwardAttachments(
  repo: ForwardRepo,
  values: readonly unknown[],
): Promise<AttachmentRef[]> {
  const refs: AttachmentRef[] = []
  for (const raw of values) {
    const value = String(raw ?? '')
    const docId = attachmentDocId(value)
    if (!docId) {
      throw new Error(
        `"${value}" is not a Mod attachment. Pass the path you were handed ` +
          `(…/${CACHE_SEGMENT}/<id>/<name>) or the attachment's document id.`,
      )
    }
    let doc: AttachmentDocShape | undefined
    try {
      const handle = await repo.find(docId as AnyDocumentId)
      await handle.whenReady?.()
      doc = handle.doc() as AttachmentDocShape | undefined
    } catch {
      doc = undefined
    }
    const mimeType = doc?.mimeType == null ? '' : String(doc.mimeType)
    if (!doc || !mimeType) {
      throw new Error(`attachment ${docId} could not be read on this machine`)
    }
    const bytes = doc.content?.byteLength ?? doc.content?.length ?? 0
    const width = Number(doc.width)
    const height = Number(doc.height)
    refs.push({
      docId: docId as AttachmentRef['docId'],
      name: doc.name == null ? 'attachment' : String(doc.name),
      mimeType,
      bytes,
      ...(width > 0 ? { width } : {}),
      ...(height > 0 ? { height } : {}),
    })
  }
  return refs
}
